import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateAcquisitionCostLineInput,
  type AcquisitionCostLine,
  type AcquisitionCostLineInput,
  type AcquisitionKind,
} from "../../domain/acquisition";
import { ValidationError } from "./projectRepository";

interface AcquisitionRow {
  id: string;
  project_id: string;
  kind: string;
  description: string;
  amount_cents: number;
  credited_toward_purchase: number;
  date: string | null;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapLine(row: AcquisitionRow): AcquisitionCostLine {
  return {
    id: row.id,
    projectId: row.project_id,
    kind: row.kind as AcquisitionKind,
    description: row.description,
    amountCents: Number(row.amount_cents),
    creditedTowardPurchase: Number(row.credited_toward_purchase) === 1,
    date: row.date,
    notes: row.notes,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const SELECT = `SELECT id, project_id, kind, description, amount_cents,
                       credited_toward_purchase, date, notes, archived_at,
                       created_at, updated_at
                  FROM acquisition_cost_lines`;

export class AcquisitionRepository {
  constructor(private readonly db: DbExecutor) {}

  async listByProject(projectId: string): Promise<AcquisitionCostLine[]> {
    const rows = await this.db.select<AcquisitionRow>(
      `${SELECT} WHERE project_id = ? ORDER BY created_at`,
      [projectId],
    );
    return rows.map(mapLine);
  }

  async create(projectId: string, input: AcquisitionCostLineInput): Promise<AcquisitionCostLine> {
    const problems = validateAcquisitionCostLineInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const now = new Date().toISOString();
    const line: AcquisitionCostLine = {
      id: generateId(),
      projectId,
      kind: input.kind,
      description: input.description.trim(),
      amountCents: input.amountCents,
      creditedTowardPurchase: input.creditedTowardPurchase,
      date: input.date,
      notes: input.notes,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO acquisition_cost_lines
         (id, project_id, kind, description, amount_cents, credited_toward_purchase,
          date, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        line.id,
        line.projectId,
        line.kind,
        line.description,
        line.amountCents,
        line.creditedTowardPurchase ? 1 : 0,
        line.date,
        line.notes,
        line.createdAt,
        line.updatedAt,
      ],
    );
    return line;
  }

  async update(id: string, input: AcquisitionCostLineInput): Promise<void> {
    const problems = validateAcquisitionCostLineInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.db.execute(
      `UPDATE acquisition_cost_lines SET
         kind = ?, description = ?, amount_cents = ?, credited_toward_purchase = ?,
         date = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.kind,
        input.description.trim(),
        input.amountCents,
        input.creditedTowardPurchase ? 1 : 0,
        input.date,
        input.notes,
        new Date().toISOString(),
        id,
      ],
    );
  }

  /** Archive (soft-delete) — the historical cash record is preserved. */
  async archive(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE acquisition_cost_lines SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), new Date().toISOString(), id],
    );
  }
}
